import React, { useState, useEffect, useCallback } from 'react';
import ActivityView from './Activity.view';
import {
  getBearerToken,
  getCurrentUser,
  getIdToken,
} from '../../common/utils/authUtils';
import { LOG } from 'application/common/Utilities';

// Updated interface to match Lambda response structure
interface ActivityItem {
  id: string;
  name: string;
  team: string;
  firstContactAfter9AM: string | null;
  thirdContactAfter9AM: string | null;
  lastBefore3pm: string | null;
  firstAfter4pm: string | null;
  thirdToLastPin: string | null;
  lastPin: string | null;
  pinsDropped: number | string;
  sales: number | string | null;
  notHomeCount: number | string | null;
  notInterestedCount: number | string | null;
  gavePitchCount: number | string | null;
  callBacksCount: number | string | null;
  percentNotInterested: number | string | null;
  percentGavePitch: number | string | null;
  percentNotHome: number | string | null;
  percentCallBack: number | string | null;
  knocksToSales: number | string | null;
  notInterestedToSales: number | string | null;
  gavePitchToSales: number | string | null;
}

// Define the expected structure of the API response body
interface ApiTeamMetric {
  name: string;
  user_id: string;
  teams: {
    circle_id: string;
    circle_name: string;
    level_id: string;
    level_name: string;
    is_direct_member: boolean;
  }[];
  sales: number | string | null;
  first_pin_after_9am: string | null;
  third_pin_after_9am: string | null;
  last_before_3pm: string | null;
  first_after_4pm: string | null;
  third_to_last_pin: string | null;
  last_pin: string | null;
  pins_dropped: number | string;
  not_home_count: number | string | null;
  not_interested_count: number | string | null;
  gave_pitch_count: number | string | null;
  call_backs_count: number | string | null;
  percent_not_interested: number | string | null;
  percent_gave_pitch: number | string | null;
  percent_not_home: number | string | null;
  percent_call_back: number | string | null;
  knocks_to_sales: number | string | null;
  not_interested_to_sales: number | string | null;
  gave_pitch_to_sales: number | string | null;
}

// Interface for KPI IDs
interface UserActivityKpis {
  notInterested: { id: string };
  knocks: { id: string };
  callBack: { id: string };
  interested: { id: string };
  gavePitch: { id: string };
  sales: { id: string };
  notHome: { id: string };
  [key: string]: { id: string };
}

// Utility function to safely access the userActivityKpis
const getUserActivityKpis = (): UserActivityKpis | undefined => {
  try {
    // We need to use a type assertion because TypeScript doesn't know about our global object
    // @ts-ignore
    if (!global.appSettings?.userActivityKpis) {
      LOG.error('No userActivityKpis found in global.appSettings');
      return undefined;
    }
    // @ts-ignore
    return global.appSettings.userActivityKpis;
  } catch (error) {
    LOG.error('Error accessing userActivityKpis:', error);
    return undefined;
  }
};

// Helper function to format values for display
const formatDisplayValue = (value: number | string | null): string | number => {
  if (value === null || value === 0 || value === '0') {
    return '--';
  }
  return value;
};

// Fetch default KPIs synchronously before component loads
const defaultKpis = getUserActivityKpis();

const ActivityContainer = () => {
  // @ts-ignore
  const user = global.me;
  const isAdmin = user?.isAdmin;
  let isManager = false;
  if (!isAdmin) {
    isManager = user?.functionalRoles?.includes('manager');
  }
  useEffect(() => {
    if (!user || (!isAdmin && !isManager)) {
      window.history.back();
    }
  }, [user, isAdmin, isManager]);

  // Check if manager has multiple teams
  const hasMultipleManagerCircles =
    isManager && user?.myManagerCircles && user.myManagerCircles.length > 1;

  // Update defaultTeamId logic to properly handle admin vs manager
  let defaultTeamId = 'all';

  // For managers with a single team, automatically select that team
  if (
    isManager &&
    user?.myManagerCircles &&
    user.myManagerCircles.length === 1
  ) {
    defaultTeamId = user.myManagerCircles[0];
  }

  // Flag to indicate if the "All teams" option should be available
  // Allow for admins and managers with multiple teams
  const allowAllTeamsOption = isAdmin || hasMultipleManagerCircles;

  const [loading, setLoading] = useState(true);
  const [activityData, setActivityData] = useState<ActivityItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Add pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(25); // Fixed at 50 users per page
  const [totalCount, setTotalCount] = useState(0);

  // Initialize with default KPIs already fetched
  const [userActivityKpis, setUserActivityKpis] = useState<
    UserActivityKpis | undefined
  >(defaultKpis);
  // Start kpisLoaded as false to prevent premature data fetching
  const [kpisLoaded, setKpisLoaded] = useState<boolean>(false);

  // Get the user's organization ID from localStorage
  const orgId = user?.orgID || null; // Fallback to None if not available
  const userId = user?.id || null; // Fallback to None if not available

  // Load userActivityKpis only once on component mount
  useEffect(() => {
    // Try to get KPIs if they weren't loaded initially
    const kpis = getUserActivityKpis();

    if (!userActivityKpis && kpis) {
      // Check if kpis were actually found
      setUserActivityKpis(kpis);
    }

    // Regardless of whether KPIs were found, mark as loaded *now*
    // This ensures we don't get stuck in loading state and allows data fetching to proceed
    setKpisLoaded(true);
  }, []); // Keep dependency array empty to run only once on mount

  // Helper function to get current local date in YYYY-MM-DD format
  const getCurrentLocalDate = () => {
    const now = new Date();
    return now.toLocaleDateString('en-CA'); // Updated to use en-CA format which returns YYYY-MM-DD
  };

  // Fetch activity data based on filters
  const fetchActivityData = useCallback(
    async (teamId?: string, date?: string) => {
      // Start with the provided team ID or default
      let targetTeamId = teamId || defaultTeamId;

      // Only override 'all' for managers with a single team
      if (isManager && targetTeamId === 'all' && !hasMultipleManagerCircles) {
        targetTeamId = user.myManagerCircles[0];
      }

      // Use current local date if none is provided
      const targetDate = date || getCurrentLocalDate();

      // Reset page to 0 when filters change
      setPage(0);

      setLoading(true);
      setError(null);

      // Try to get fresh KPIs directly from global object
      const freshKpis = getUserActivityKpis();
      if (
        freshKpis &&
        (!userActivityKpis ||
          Object.keys(freshKpis).length > Object.keys(userActivityKpis).length)
      ) {
        setUserActivityKpis(freshKpis);
      }

      try {
        // First check if user is authenticated
        const currentUser = await getCurrentUser();
        if (!currentUser) {
          throw new Error('You are not authenticated. Please sign in again.');
        }

        const url = 'https://api.gogamify.com/activity/team';

        // Try with access token first, then ID token if needed
        const authResult = await tryFetchWithToken(
          url,
          targetTeamId,
          targetDate,
          orgId,
          false,
          userId
        );

        // Process the result
        if (authResult.status !== 200) {
          throw new Error(authResult.errorMessage || 'Failed to fetch data');
        }

        const response = authResult.data;

        // Determine display team name
        const displayTeam =
          targetTeamId === 'all' ? `All Teams` : response.team_name;

        const transformedData = response.team_metrics.map(
          (metric: ApiTeamMetric, index: number) => {
            return {
              id: `${targetTeamId}-${targetDate}-${index}`,
              name: metric.name || '--',
              team: displayTeam,
              firstContactAfter9AM: metric.first_pin_after_9am,
              thirdContactAfter9AM: metric.third_pin_after_9am,
              lastBefore3pm: metric.last_before_3pm,
              firstAfter4pm: metric.first_after_4pm,
              thirdToLastPin: metric.third_to_last_pin,
              lastPin: metric.last_pin,
              pinsDropped: formatDisplayValue(metric.pins_dropped),
              // Add new fields, even if they might be unused when KPIs aren't available
              sales: formatDisplayValue(metric.sales),
              notHomeCount: formatDisplayValue(metric.not_home_count),
              notInterestedCount: formatDisplayValue(
                metric.not_interested_count
              ),
              gavePitchCount: formatDisplayValue(metric.gave_pitch_count),
              callBacksCount: formatDisplayValue(metric.call_backs_count),
              percentNotInterested: formatDisplayValue(
                metric.percent_not_interested
              ),
              percentGavePitch: formatDisplayValue(metric.percent_gave_pitch),
              percentNotHome: formatDisplayValue(metric.percent_not_home),
              percentCallBack: formatDisplayValue(metric.percent_call_back),
              knocksToSales: formatDisplayValue(metric.knocks_to_sales),
              notInterestedToSales: formatDisplayValue(
                metric.not_interested_to_sales
              ),
              gavePitchToSales: formatDisplayValue(metric.gave_pitch_to_sales),
            };
          }
        );

        if (transformedData.length === 0) {
          setError(
            'There is no data for the selected date or team. Try changing your filters.'
          );
          setActivityData([]);
        } else {
          setActivityData(transformedData);
          setError(null);
        }
        setTotalCount(transformedData.length);
        setLoading(false);
      } catch (err: any) {
        setError(
          err.message || 'An unexpected error occurred. Please try again.'
        );
        setActivityData([]);
        setLoading(false);
      }
    },
    [
      orgId,
      userActivityKpis,
      isManager,
      user,
      defaultTeamId,
      hasMultipleManagerCircles,
    ]
  );

  // Helper function to try fetch with either access token or ID token
  const tryFetchWithToken = async (
    url: string,
    teamId: string,
    date: string,
    orgId: string | null,
    useIdToken: boolean,
    userId: string | null
  ) => {
    try {
      // Get the token based on the useIdToken flag
      const token = useIdToken ? await getIdToken() : await getBearerToken();

      if (!token) {
        return {
          status: 401,
          errorMessage:
            'Authentication token is missing. Please sign in again.',
          data: null,
        };
      }

      // Determine user role for backend
      const userRole = isAdmin ? 'admin' : 'manager';

      // Build request body — only include activity_kpis when KPIs are configured
      const requestBody: Record<string, any> = {
        team_id: teamId,
        user_id: userId,
        date,
        org_id: orgId,
        user_role: userRole,
      };

      // Only include activity_kpis if they are actually configured for this org
      if (userActivityKpis && Object.keys(userActivityKpis).length > 0) {
        requestBody.activity_kpis = userActivityKpis;
      }

      // Create request options - remove client-side CORS headers
      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      };

      // Add a timeout to the fetch request
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15-second timeout

      const httpResponse = await fetch(url, {
        ...requestOptions,
        signal: controller.signal,
      });

      // Clear the timeout
      clearTimeout(timeoutId);

      // Get detailed error info if possible
      let errorDetails = '';
      if (!httpResponse.ok) {
        try {
          const errorResponse = await httpResponse.json();
          errorDetails = JSON.stringify(errorResponse);
        } catch (e) {
          // If we can't parse the error as JSON, try to get text
          try {
            errorDetails = await httpResponse.text();
            LOG.error('API error text:', errorDetails);
          } catch (textError) {
            LOG.error('Could not read error response:', textError);
          }
        }
      }

      if (!httpResponse.ok) {
        return {
          status: httpResponse.status,
          errorMessage: `Error ${httpResponse.status}: ${
            httpResponse.statusText || 'Data is not available'
          }. Details: ${errorDetails}`,
          data: null,
        };
      }

      const responseData = await httpResponse.json();
      return {
        status: 200,
        errorMessage: null,
        data: responseData,
      };
    } catch (error: any) {
      return {
        status: 500,
        errorMessage: error.message || 'Network error',
        data: null,
      };
    }
  };

  // Combined useEffect that waits for KPIs to be loaded before fetching data
  useEffect(() => {
    // Only fetch data if KPIs have been checked (kpisLoaded is true)
    // and orgId is available (assuming it's necessary)
    if (kpisLoaded && orgId) {
      fetchActivityData(defaultTeamId, getCurrentLocalDate());
    }
    // Note: userActivityKpis intentionally omitted — KPIs are optional and
    // should not trigger re-fetches. The basic activity data should load regardless.
  }, [fetchActivityData, kpisLoaded, orgId, defaultTeamId]);

  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  return (
    <ActivityView
      loading={loading}
      activityData={activityData}
      error={error}
      refreshData={fetchActivityData}
      page={page}
      rowsPerPage={rowsPerPage}
      totalCount={totalCount}
      onPageChange={handleChangePage}
      onRowsPerPageChange={() => {}} // No-op since rowsPerPage is fixed
      defaultTeamId={defaultTeamId}
      allowAllTeamsOption={allowAllTeamsOption}
      isManager={isManager}
      userActivityKpis={userActivityKpis}
    />
  );
};

export default ActivityContainer;
